// pages/public/TableOrderPage.jsx — page PUBLIQUE (pas de compte requis)
// Ouverte en scannant le QR code d'une table. Route à monter SANS
// ProtectedRoute, ex: <Route path="/commande/:qrToken" element={<TableOrderPage/>}/>
import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicOrderApi, stripeApi } from '../../utils/api';
import { Plus, Minus, ShoppingCart, X, CheckCircle2 } from 'lucide-react';
import EmbeddedStripeCheckout from '../../components/EmbeddedStripeCheckout';

// Le prix Happy Hour (s'il est actif pour ce produit) prime automatiquement
// sur le prix normal — même logique que le Mode Caisse. `variant` ajoute son
// écart de prix (souvent 0 €) par-dessus.
function effectivePrice(p, variant) {
  const base = p.happy_hour_active ? parseFloat(p.happy_hour_price) : parseFloat(p.sell_price ?? p.price ?? 0);
  return base + (variant ? parseFloat(variant.priceDelta || 0) : 0);
}

// Clé unique d'une ligne de panier : un même produit avec deux déclinaisons
// différentes occupe deux lignes distinctes.
const cartKey = (productId, variantId) => variantId ? `${productId}::${variantId}` : productId;

export default function TableOrderPage() {
  const { qrToken } = useParams();
  const [searchParams] = useSearchParams();
  const paidFlag = searchParams.get('paid') === '1';
  const orderIdFromUrl = searchParams.get('order');

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [table, setTable]       = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCats]   = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [cart, setCart]         = useState([]);
  const [note, setNote]         = useState('');
  const [showCart, setShowCart] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [variantPicker, setVariantPicker] = useState(null); // produit en cours de sélection de déclinaison
  const [stripePubKey, setStripePubKey] = useState(null);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);

  useEffect(() => {
    publicOrderApi.menu(qrToken)
      .then(r => { setTable(r.data.table); setProducts(r.data.products); setCats(r.data.categories); })
      .catch(e => setError(e.response?.data?.error || "Cette table n'est pas disponible"))
      .finally(() => setLoading(false));
    stripeApi.config().then(r => { if (r.data.enabled) setStripePubKey(r.data.publishableKey); }).catch(() => {});
  }, [qrToken]);

  useEffect(() => {
    if (paidFlag && orderIdFromUrl) {
      publicOrderApi.status(qrToken, orderIdFromUrl)
        .then(r => setOrderStatus(r.data.order))
        .catch(() => {});
    }
  }, [paidFlag, orderIdFromUrl, qrToken]);

  const addToCart = (p, variant = null) => {
    if (!variant && Array.isArray(p.variants) && p.variants.length > 0) {
      setVariantPicker(p);
      return;
    }
    const key = cartKey(p.id, variant?.id);
    setCart(prev => {
      const ex = prev.find(i => cartKey(i.product.id, i.variant?.id) === key);
      if (ex) return prev.map(i => cartKey(i.product.id,i.variant?.id) === key ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product: p, variant: variant || null, qty: 1 }];
    });
    setVariantPicker(null);
  };
  const updateQty = (key, delta) => {
    setCart(prev => prev.map(i => cartKey(i.product.id,i.variant?.id) === key ? { ...i, qty: i.qty + delta } : i).filter(i => i.qty > 0));
  };

  const total = useMemo(() =>
    cart.reduce((s,i) => s + i.qty * effectivePrice(i.product, i.variant), 0),
  [cart]);
  const cartCount = cart.reduce((s,i) => s+i.qty, 0);

  const filtered = products.filter(p => !filterCat || String(p.category_id) === filterCat);

  const submitOrder = async () => {
    if (!cart.length) return;
    setSubmitting(true);
    try {
      const r = await publicOrderApi.checkout(qrToken, {
        items: cart.map(i => ({ productId: i.product.id, qty: i.qty, variantId: i.variant?.id || undefined })),
        note,
      });
      // Affiche le paiement en iframe sur cette même page, au lieu de
      // rediriger vers stripe.com.
      setCheckoutClientSecret(r.data.clientSecret);
      setShowCart(false);
    } catch(e) {
      setError(e.response?.data?.error || 'Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={S.center}>Chargement…</div>;
  if (error && !table) return <div style={S.center}><p>{error}</p></div>;

  // ── Écran de confirmation après retour de Stripe ──
  if (paidFlag) {
    return (
      <div style={S.page}>
        <div style={{ ...S.center, flexDirection:'column', gap:14 }}>
          <CheckCircle2 size={48} color="#00d2b4"/>
          <h2 style={{ margin:0 }}>Paiement confirmé !</h2>
          <p style={{ color:'#888', textAlign:'center', maxWidth:280 }}>
            Votre commande a été transmise en cuisine/bar. Elle arrive à votre table{table ? ` (${table.label})` : ''}.
          </p>
          {orderStatus && (
            <div style={{ fontSize:13, color:'#aaa' }}>Statut : {statusLabel(orderStatus.status)}</div>
          )}
        </div>
      </div>
    );
  }
  // ── Paiement en cours : iframe Stripe intégrée directement sur cette page ──
  if (checkoutClientSecret) {
    return (
      <div style={S.page}>
        <div style={{ padding:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ margin:0 }}>Paiement sécurisé</h3>
            <button onClick={() => setCheckoutClientSecret(null)} style={S.iconBtn}><X size={18}/></button>
          </div>
          <div style={{ background:'#fff', borderRadius:14, overflow:'hidden' }}>
            <EmbeddedStripeCheckout publishableKey={stripePubKey} clientSecret={checkoutClientSecret}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={{ fontSize:12, color:'#888', textTransform:'uppercase', letterSpacing:'0.06em' }}>
            {table?.label}
          </div>
          <h1 style={{ margin:0, fontSize:20 }}>Commander</h1>
        </div>
        <button style={S.cartBtn} onClick={() => setShowCart(true)}>
          <ShoppingCart size={18}/>
          {cartCount > 0 && <span style={S.cartBadge}>{cartCount}</span>}
        </button>
      </div>

      {error && <div style={{ color:'#ff4d6d', padding:'8px 16px', fontSize:13 }}>{error}</div>}

      <div style={{ display:'flex', gap:8, overflowX:'auto', padding:'10px 16px' }}>
        <button onClick={() => setFilterCat('')}
          style={filterCat === '' ? S.catChipActive : S.catChip}>Tout</button>
        {categories.map(c => (
          <button key={c.id} onClick={() => setFilterCat(String(c.id))}
            style={filterCat === String(c.id) ? S.catChipActive : S.catChip}>
            {c.icon} {c.name}
          </button>
        ))}
      </div>

      <div style={{ padding:'0 16px 100px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10 }}>
        {filtered.map(p => {
          const inCart = cart.filter(i => i.product.id === p.id).reduce((s,i)=>s+i.qty, 0);
          const price = effectivePrice(p);
          const basePrice = parseFloat(p.sell_price ?? p.price ?? 0);
          const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
          return (
            <div key={p.id} onClick={() => addToCart(p)} style={S.card(inCart > 0)}>
              {inCart > 0 && <div style={S.qtyBadge}>{inCart}</div>}
              {p.happy_hour_active && <div style={S.hhBadge}>⚡ HH</div>}
              <div style={{ fontSize:26, marginBottom:6 }}>{p.icon || '📦'}</div>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>
                {p.name}{p.unit_label && <span style={{ fontWeight:400, color:'#888' }}> {p.unit_label}</span>}
              </div>
              {hasVariants && (
                <div style={{ fontSize:11, color:'#00d2b4', fontWeight:600, marginBottom:2 }}>
                  {p.variant_group_label || 'Choix'} ▸
                </div>
              )}
              <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
                <div style={{ fontWeight:800, fontSize:15, color: p.happy_hour_active ? '#00d2b4' : '#f2f2f5' }}>
                  {price.toFixed(2)} €
                </div>
                {p.happy_hour_active && (
                  <div style={{ fontSize:11, color:'#888', textDecoration:'line-through' }}>
                    {basePrice.toFixed(2)} €
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {cartCount > 0 && !showCart && (
        <button style={S.floatingBar} onClick={() => setShowCart(true)}>
          Voir le panier · {cartCount} article{cartCount>1?'s':''} · {total.toFixed(2)} €
        </button>
      )}

      {showCart && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowCart(false)}>
          <div style={S.sheet}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ margin:0 }}>Votre panier</h3>
              <button onClick={() => setShowCart(false)} style={S.iconBtn}><X size={18}/></button>
            </div>
            {cart.length === 0 ? (
              <p style={{ color:'#888', textAlign:'center', padding:'30px 0' }}>Panier vide</p>
            ) : (
              <>
                <div style={{ maxHeight:'40vh', overflowY:'auto' }}>
                  {cart.map(i => {
                    const price = effectivePrice(i.product, i.variant);
                    const key = cartKey(i.product.id, i.variant?.id);
                    return (
                      <div key={key} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #2a2a35' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:13 }}>
                            {i.product.name}{i.variant && <span style={{ color:'#00d2b4' }}> · {i.variant.name}</span>}
                          </div>
                          <div style={{ fontSize:12, color:'#888' }}>{price.toFixed(2)} €</div>
                        </div>
                        <button style={S.stepBtn} onClick={() => updateQty(key,-1)}><Minus size={12}/></button>
                        <span style={{ minWidth:18, textAlign:'center', fontWeight:700 }}>{i.qty}</span>
                        <button style={S.stepBtn} onClick={() => updateQty(key,1)}><Plus size={12}/></button>
                      </div>
                    );
                  })}
                </div>
                <input placeholder="Une précision pour votre commande ? (optionnel)"
                  value={note} onChange={e => setNote(e.target.value)}
                  style={S.noteInput}/>
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, fontSize:18, margin:'14px 0' }}>
                  <span>Total</span><span>{total.toFixed(2)} €</span>
                </div>
                <button style={S.primaryBtn} disabled={submitting} onClick={submitOrder}>
                  {submitting ? 'Chargement…' : 'Payer par carte'}
                </button>
                <p style={{ fontSize:11, color:'#888', textAlign:'center', marginTop:8 }}>
                  Paiement sécurisé par Stripe — CB uniquement
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {variantPicker && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setVariantPicker(null)}>
          <div style={S.sheet}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ margin:0 }}>{variantPicker.name} — {variantPicker.variant_group_label || 'Choix'}</h3>
              <button onClick={() => setVariantPicker(null)} style={S.iconBtn}><X size={18}/></button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {(variantPicker.variants||[]).map(v => (
                <button key={v.id} onClick={() => addToCart(variantPicker, v)}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    width:'100%', padding:'14px', borderRadius:12, border:'1px solid #2a2a35',
                    background:'#22222e', color:'#fff', cursor:'pointer', fontSize:14 }}>
                  <span>{v.name}</span>
                  <span style={{ fontWeight:800 }}>{effectivePrice(variantPicker, v).toFixed(2)} €</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function statusLabel(s) {
  return { pending_payment:'En attente de paiement', paid:'Reçue', preparing:'En préparation',
    ready:'Prête', served:'Servie', cancelled:'Annulée' }[s] || s;
}

const S = {
  page: { minHeight:'100vh', background:'#14141c', color:'#f2f2f5', fontFamily:'system-ui,sans-serif' },
  center: { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px' },
  cartBtn: { position:'relative', background:'#22222e', border:'none', borderRadius:12, width:44, height:44,
    display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', cursor:'pointer' },
  cartBadge: { position:'absolute', top:-4, right:-4, background:'#7c5cfc', borderRadius:99, fontSize:10,
    fontWeight:800, minWidth:18, height:18, display:'flex', alignItems:'center', justifyContent:'center' },
  catChip: { flexShrink:0, padding:'6px 14px', borderRadius:99, border:'1px solid #2a2a35', background:'transparent',
    color:'#aaa', fontSize:13, cursor:'pointer' },
  catChipActive: { flexShrink:0, padding:'6px 14px', borderRadius:99, border:'1px solid #7c5cfc',
    background:'rgba(124,92,252,0.15)', color:'#c4b5fd', fontSize:13, fontWeight:700, cursor:'pointer' },
  card: (active) => ({ position:'relative', borderRadius:14, padding:'14px 12px', cursor:'pointer',
    border:`1.5px solid ${active ? '#7c5cfc' : '#2a2a35'}`, background: active ? 'rgba(124,92,252,0.1)' : '#1c1c26' }),
  qtyBadge: { position:'absolute', top:8, right:8, background:'#7c5cfc', borderRadius:99, width:20, height:20,
    fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' },
  hhBadge: { position:'absolute', top:8, left:8, fontSize:9, fontWeight:800, color:'#fff',
    background:'#00d2b4', padding:'2px 7px', borderRadius:99, letterSpacing:'0.02em' },
  floatingBar: { position:'fixed', bottom:16, left:16, right:16, background:'#7c5cfc', color:'#fff', border:'none',
    borderRadius:14, padding:'14px', fontWeight:700, fontSize:14, cursor:'pointer', boxShadow:'0 8px 24px rgba(124,92,252,0.4)' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:50 },
  sheet: { background:'#1c1c26', width:'100%', borderRadius:'20px 20px 0 0', padding:20, maxHeight:'80vh', overflowY:'auto' },
  iconBtn: { background:'none', border:'none', color:'#888', cursor:'pointer' },
  stepBtn: { width:24, height:24, borderRadius:6, border:'1px solid #2a2a35', background:'none', color:'#fff', cursor:'pointer' },
  noteInput: { width:'100%', boxSizing:'border-box', background:'#14141c', border:'1px solid #2a2a35', borderRadius:10,
    padding:'10px 12px', color:'#fff', fontSize:13, marginTop:10 },
  primaryBtn: { width:'100%', padding:'14px', borderRadius:12, border:'none', background:'#7c5cfc', color:'#fff',
    fontWeight:700, fontSize:15, cursor:'pointer' },
};
